import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  Alert,
  EmptyIndicator,
  FlexContainer,
  MoreOptions
} from 'components/common';
import { Box, BoxBackLink, BoxRow } from 'components/common/box';
import {
  AchievementCompletionClosingApprovalHistory,
  AchievementCompletionsFillTemplateConversation,
  AchievementCompletionsFillTemplateTasklist,
  AchievementCompletionsUpdate,
  AchievementUserCompletionData,
  AchievementUserPreviousCompletions,
  AchievementCompletionsSetConfidential,
  AchievementCompletionsCloseConversation,
  AchievementCompletionItemsCleared
} from '.';

import type { GetAchievementCompletionItemsRes } from 'shared-types/responses';

interface Props {
  userId: number;
}

const AchievementUsersUser = ({ userId }: Props) => {
  const {
    messages,
    pageState: { achievement },
    theme,
    user: loggedInUser
  } = useReduxState();

  const { hasTemplate, templateHeader } = useAchievementTypes({
    type: achievement?.type
  });

  const { data, loading, updateData } =
    useApiData<GetAchievementCompletionItemsRes>({
      onlyIf: Boolean(achievement.id && userId),
      url: `/admin/achievements/${achievement.id}/completions/${userId}/items`
    });

  const achievementData = data?.achievementData;
  const targetUser = data?.user;
  const userItems = data?.items;

  const components = {
    conversation: AchievementCompletionsFillTemplateConversation,
    tasklist: AchievementCompletionsFillTemplateTasklist
  };
  const Element = components[achievement.type] as
    | typeof AchievementCompletionsFillTemplateConversation
    | typeof AchievementCompletionsFillTemplateTasklist;

  const initialValues = {};
  achievement.template.forEach((item) => {
    initialValues[`${item.id}`] = {
      comment: userItems?.[item.id]?.text || null
    };
  });

  const isSuperadmin =
    achievement.isOwnerOrSuperadmin ||
    loggedInUser?.roles.includes('superadmin');

  const canAnswerAndMark =
    isSuperadmin ||
    (achievement.isSupervisor && data?.isLoggedUserSupervisor) ||
    achievement.userAdminPermissions?.includes('manage-users');

  const doNotShowCloseConversation =
    !canAnswerAndMark ||
    !achievement.allowClosingConversation ||
    achievement.type !== 'conversation' ||
    (data?.achievementData && data?.achievementData?.status !== 'completed');

  return targetUser?.isAnonymous ? (
    <Box>
      <EmptyIndicator icon="lock" title={messages.errorForbidden} />
    </Box>
  ) : (
    <>
      <Box
        headerContent={
          <FlexContainer style={{ justifyContent: 'space-between' }}>
            <BoxBackLink simpleReturn={true} />

            {achievementData && achievementData.status !== 'not started' ? (
              <MoreOptions styleContentButtons={false}>
                <AchievementCompletionsUpdate
                  isDropdown={true}
                  user={{ ...targetUser, achievementData }}
                  updateUsers={updateData}
                />
              </MoreOptions>
            ) : null}
          </FlexContainer>
        }
      >
        {targetUser ? (
          <div className="summary">
            <AchievementUserCompletionData
              achievementData={achievementData}
              user={targetUser}
            />

            <style jsx>{`
              .summary {
                padding: 10px 0;
              }
            `}</style>
          </div>
        ) : null}
      </Box>

      <AchievementUserPreviousCompletions
        achievement={achievement}
        previousCompletions={data?.previousCompletions}
        style={{ marginTop: 25 }}
        userId={userId}
      />

      {hasTemplate ? (
        <Box
          loading={loading}
          padding={0}
          style={{ marginTop: 25 }}
          title={
            <FlexContainer
              style={{ justifyContent: 'space-between', gap: 7, width: '100%' }}
            >
              <div style={{ display: 'flex', gap: 7 }}>
                <div>{templateHeader}</div>

                <div
                  style={{
                    color: theme.textColorLighter,
                    display: 'inline-block',
                    fontSize: '1.5rem',
                    fontWeight: 600
                  }}
                >
                  {achievementData?.canSeeCompletionItems
                    ? `(${achievementData?.numberOfCompletedTemplateItems}/${achievement.template.length})`
                    : ''}
                </div>
              </div>

              <AchievementCompletionsSetConfidential
                achievementId={achievement.id}
                achievementCompletionId={achievementData?.id}
                disabled={!achievement.allowConfidential || !canAnswerAndMark}
                isConfidential={achievementData?.isConfidential}
                isUserOrMember={targetUser?.id === loggedInUser?.id}
                users={[targetUser]}
                updateAchievement={updateData}
                {...(targetUser?.id !== loggedInUser?.id
                  ? { targetUserId: userId }
                  : {})}
              />
            </FlexContainer>
          }
        >
          {achievementData?.completionItemsClearedDate ? (
            <AchievementCompletionItemsCleared
              date={achievementData.completionItemsClearedDate}
              type={achievement.type}
            />
          ) : achievementData?.canSeeCompletionItems ? (
            <>
              {achievement.template?.map((item, i) => {
                const answer = userItems[item.id];
                const hasBeenAnswered = Boolean(answer?.id);

                // this component is only accessible through an admin route
                // and only affects non-group achievements, so there will
                // always only be one answer per item
                const canEditAnswer =
                  isSuperadmin ||
                  (hasBeenAnswered &&
                    (answer?.user?.id === loggedInUser?.id ||
                      answer?.user?.id === userId));

                const hasEditingRights =
                  !achievementData?.isClosed &&
                  (hasBeenAnswered ? canEditAnswer : canAnswerAndMark);

                return (
                  <Element
                    key={item.id}
                    achievement={achievement}
                    adminMode={true}
                    canAnswer={hasEditingRights}
                    canMarkComplete={hasEditingRights}
                    item={{
                      id: item.id,
                      isAnswerableByUser: item.isAnswerableByUser,
                      isCompleted: Boolean(answer?.id),
                      isCompletedOn: answer?.date ?? null,
                      isCompletedMarkedByUser: answer?.user ?? null,
                      isMarkableByUser: item.isMarkableByUser,
                      question: item.text,
                      answers: answer ? [answer] : []
                    }}
                    itemIndex={i + 1}
                    targetUserId={userId}
                    updateItems={updateData}
                  />
                );
              })}

              {doNotShowCloseConversation ? null : (
                <BoxRow>
                  {achievementData.isClosed && (
                    <Alert
                      type="warning-light"
                      text={messages.achievementConversationClosed}
                    />
                  )}
                  <AchievementCompletionsCloseConversation
                    achievement={achievement}
                    achievementData={achievementData}
                    achievementId={achievement.id}
                    completionIdentifier={userId}
                    isUserOrMember={userId === loggedInUser?.id}
                    updateData={updateData}
                  />
                  <AchievementCompletionClosingApprovalHistory
                    achievement={achievement}
                    achievementData={achievementData}
                  />
                </BoxRow>
              )}
            </>
          ) : (
            <EmptyIndicator
              description={messages.achievementCompletionConfidentialInfo}
              icon="lock"
              title={messages.commonConfidential}
            />
          )}
        </Box>
      ) : null}
    </>
  );
};

export default AchievementUsersUser;
